import React, { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Fade } from '@mui/material';
import QuestionField from './QuestionFields/QuestionField';

import type { CampaignQuestion, OptionsFieldError } from '../types';
import { useAutoScrollToBottom } from '@/hooks';

interface SortableQuestionItemProps {
    question: CampaignQuestion;
    index: number;
    questionsToRemove: number[];
    onQuestionDelete: (index: number) => void;
    onQuestionReorder: (activeIndex: number, overIndex: number) => void;
    error?: OptionsFieldError;
    highlightedQuestionId?: string | null;
    isFirst: boolean;
    isLast: boolean;
    isSortingDisabled?: boolean;
}

/**
 * Wrapper component that makes a question item sortable via drag and drop in edit mode.
 */
const SortableQuestionItem: React.FC<SortableQuestionItemProps> = (props) => {
    const {
        question,
        index,
        questionsToRemove,
        onQuestionDelete,
        onQuestionReorder,
        error,
        highlightedQuestionId,
        isFirst,
        isLast,
        isSortingDisabled = false,
    } = props;

    const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: question.id || `question-${index}`,
        disabled: isSortingDisabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleMoveUp = useCallback(() => {
        if (!isFirst) {
            onQuestionReorder(index, index - 1);
        }
    }, [isFirst, onQuestionReorder, index]);

    const handleMoveDown = useCallback(() => {
        if (!isLast) {
            onQuestionReorder(index, index + 1);
        }
    }, [isLast, onQuestionReorder, index]);

    const handleDelete = useCallback(() => {
        onQuestionDelete(index);
    }, [onQuestionDelete, index]);

    const isHighlighted = highlightedQuestionId === (question.id || `question-${index}`);
    const setRefs = useAutoScrollToBottom<HTMLDivElement>(isHighlighted, setNodeRef);

    return (
        <Fade in={!questionsToRemove.includes(index)} timeout={500} unmountOnExit>
            <Box ref={setRefs} style={style}>
                <QuestionField
                    question={question}
                    onDelete={handleDelete}
                    index={index + 1}
                    error={error}
                    dragHandleProps={isSortingDisabled ? undefined : listeners}
                    onMoveUp={isSortingDisabled ? undefined : handleMoveUp}
                    onMoveDown={isSortingDisabled ? undefined : handleMoveDown}
                    isFirst={isFirst}
                    isLast={isLast}
                    isHighlighted={isHighlighted}
                    fieldIndex={index}
                />
            </Box>
        </Fade>
    );
};

export default React.memo(SortableQuestionItem);
